import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { Table, CombinedTable, Reservation, TableAvailability, TableWeekdayAvailability } from "@/types/table";
import { Check, Users, AlertTriangle, Layers, ArrowRightLeft, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { isTableAvailableOnDate } from "@/lib/tableAvailabilityUtils";
import { getBlockedTableIds, getOccupiedTableMap, OccupiedTableEntry, ConflictCheckOptions } from "@/lib/reservationConflictUtils";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { TableSwapConfirmDialog } from "@/components/TableSwapConfirmDialog";
import { useServiceSlots } from "@/hooks/useServiceTimeCompare";
import { hasOvernight } from "@/lib/serviceOrder";

interface QuickTableChangeDialogProps {
  reservation: {
    id: number;
    supabaseId?: string;
    tableId?: string;
    combinedTableId?: string;
    persons: number;
    time: string;
    reservationDate: Date;
    customerName?: string;
    isLocked?: boolean;
  };
  tables: Table[];
  combinedTables: CombinedTable[];
  allReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  onTableChange: (reservationId: number, tableId: string | null, combinedTableId: string | null) => void;
  /**
   * When provided, selecting a table that is occupied by another reservation
   * triggers a swap (via RPC `swap_reservation_tables`) instead of being hidden.
   */
  onSwapTables?: (aSupabaseId: string, bSupabaseId: string) => Promise<void> | void;
  children: React.ReactNode;
  disabled?: boolean;
  reservationSettings?: {
    bookingMode: 'slot' | 'time';
    reservationDurationMinutes: number;
  };
}

interface OptionRow {
  id: string;
  name: string;
  capacity: number;
  isCombined: boolean;
  isCurrent: boolean;
  occupiedBy: OccupiedTableEntry | null;
}

export const QuickTableChangeDialog = ({
  reservation,
  tables,
  combinedTables,
  allReservations,
  availabilities,
  weekdayAvailabilities,
  onTableChange,
  onSwapTables,
  children,
  disabled = false,
  reservationSettings,
}: QuickTableChangeDialogProps) => {
  const [open, setOpen] = useState(false);
  const [pendingSwap, setPendingSwap] = useState<{
    target: OccupiedTableEntry;
    targetTableName: string;
  } | null>(null);
  const [swapSubmitting, setSwapSubmitting] = useState(false);

  const serviceSlots = useServiceSlots();
  const conflictOptions: ConflictCheckOptions | undefined = reservationSettings ? {
    bookingMode: reservationSettings.bookingMode,
    reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
    serviceSlots,
  } : undefined;

  const otherReservations = useMemo(() => allReservations.filter(r => {
    const resDate = r.date instanceof Date ? formatDateLocal(r.date) : r.date;
    const currentDate = formatDateLocal(reservation.reservationDate);
    // Service de nuit : garder aussi la veille (utilisée seulement si un créneau franchit minuit)
    const prev = new Date(reservation.reservationDate); prev.setDate(prev.getDate() - 1);
    const sameOrPrev = resDate === currentDate || (hasOvernight(serviceSlots) && resDate === formatDateLocal(prev));
    return sameOrPrev &&
      (reservation.supabaseId
        ? r.supabaseId !== reservation.supabaseId
        : r.id !== reservation.id.toString()) &&
      r.status !== 'annulé';
  }), [allReservations, reservation]);

  const blockedTableIds = useMemo(
    () => getBlockedTableIds(otherReservations, combinedTables, reservation.reservationDate, reservation.time, conflictOptions),
    [otherReservations, combinedTables, reservation.reservationDate, reservation.time, conflictOptions],
  );

  const occupiedMap = useMemo(
    () => getOccupiedTableMap(otherReservations, combinedTables, reservation.reservationDate, reservation.time, conflictOptions),
    [otherReservations, combinedTables, reservation.reservationDate, reservation.time, conflictOptions],
  );

  const swapEnabled = !!onSwapTables && !!reservation.supabaseId && !reservation.isLocked;

  const tableRows: OptionRow[] = useMemo(() => {
    return tables
      .filter(t => {
        if (!t.isActive) return false;
        const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === t.id) || null;
        if (!isTableAvailableOnDate(t, reservation.reservationDate, availabilities, weekdayAvail)) return false;
        const occ = occupiedMap.get(t.id) ?? null;
        if (occ) {
          // Hide occupied options unless swap is supported and target is swappable
          if (!swapEnabled) return false;
          if (occ.isLocked) return false;
          if (occ.status === 'annulé' || occ.status === 'en_attente') return false;
          if (!occ.reservationSupabaseId) return false;
        } else if (blockedTableIds.has(t.id)) {
          // Blocked but no direct occupant entry (e.g. member of an occupied combined) → still hide
          return false;
        }
        return true;
      })
      .map(t => ({
        id: t.id,
        name: t.name,
        capacity: t.capacity,
        isCombined: false,
        isCurrent: reservation.tableId === t.id,
        occupiedBy: occupiedMap.get(t.id) ?? null,
      }))
      .sort((a, b) => {
        // Free first, then by capacity fit
        if (!!a.occupiedBy !== !!b.occupiedBy) return a.occupiedBy ? 1 : -1;
        const diffA = a.capacity - reservation.persons;
        const diffB = b.capacity - reservation.persons;
        if (diffA >= 0 && diffB < 0) return -1;
        if (diffA < 0 && diffB >= 0) return 1;
        return Math.abs(diffA) - Math.abs(diffB);
      });
  }, [tables, reservation, availabilities, weekdayAvailabilities, blockedTableIds, occupiedMap, swapEnabled]);

  const combinedRows: OptionRow[] = useMemo(() => {
    return combinedTables
      .filter(ct => {
        if (!ct.isActive) return false;
        const allDateAvailable = ct.tableIds.every(tableId => {
          const table = tables.find(t => t.id === tableId);
          if (!table) return false;
          const weekdayAvail = weekdayAvailabilities.find(w => w.tableId === tableId) || null;
          return isTableAvailableOnDate(table, reservation.reservationDate, availabilities, weekdayAvail);
        });
        if (!allDateAvailable) return false;
        const occ = occupiedMap.get(ct.id) ?? null;
        if (occ) {
          if (!swapEnabled) return false;
          if (occ.isLocked) return false;
          if (occ.status === 'annulé' || occ.status === 'en_attente') return false;
          if (!occ.reservationSupabaseId) return false;
        } else if (blockedTableIds.has(ct.id) || ct.tableIds.some(tid => blockedTableIds.has(tid))) {
          return false;
        }
        return true;
      })
      .map(ct => ({
        id: ct.id,
        name: ct.name,
        capacity: ct.totalCapacity,
        isCombined: true,
        isCurrent: reservation.combinedTableId === ct.id,
        occupiedBy: occupiedMap.get(ct.id) ?? null,
      }))
      .sort((a, b) => {
        if (!!a.occupiedBy !== !!b.occupiedBy) return a.occupiedBy ? 1 : -1;
        const diffA = a.capacity - reservation.persons;
        const diffB = b.capacity - reservation.persons;
        if (diffA >= 0 && diffB < 0) return -1;
        if (diffA < 0 && diffB >= 0) return 1;
        return Math.abs(diffA) - Math.abs(diffB);
      });
  }, [combinedTables, tables, reservation, availabilities, weekdayAvailabilities, blockedTableIds, occupiedMap, swapEnabled]);

  const handleSelect = (row: OptionRow) => {
    if (row.isCurrent) return;
    if (row.occupiedBy) {
      // Trigger swap flow
      if (!swapEnabled || !row.occupiedBy.reservationSupabaseId) return;
      setPendingSwap({ target: row.occupiedBy, targetTableName: row.name });
      return;
    }
    if (row.isCombined) {
      onTableChange(reservation.id, null, row.id);
    } else {
      onTableChange(reservation.id, row.id, null);
    }
    setOpen(false);
  };

  const confirmSwap = async () => {
    if (!pendingSwap || !onSwapTables || !reservation.supabaseId) return;
    const targetSupabaseId = pendingSwap.target.reservationSupabaseId;
    if (!targetSupabaseId) return;
    try {
      setSwapSubmitting(true);
      await onSwapTables(reservation.supabaseId, targetSupabaseId);
      setPendingSwap(null);
      setOpen(false);
    } catch {
      // toast already shown by parent handler; keep dialog open for retry
    } finally {
      setSwapSubmitting(false);
    }
  };

  const getCapacityBadge = (capacity: number) => {
    const diff = capacity - reservation.persons;
    if (diff === 0) {
      return <Badge className="bg-green-500/20 text-green-700 dark:text-green-400 border-green-500/30 text-[10px]">Parfait</Badge>;
    }
    if (diff === 1) {
      return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-400 border-blue-500/30 text-[10px]">+1</Badge>;
    }
    if (diff > 1) {
      return <Badge variant="secondary" className="text-[10px]">+{diff}</Badge>;
    }
    return <Badge variant="destructive" className="text-[10px]">{diff}</Badge>;
  };

  if (disabled) {
    return <>{children}</>;
  }

  const renderOption = (row: OptionRow) => (
    <Button
      key={row.id}
      variant="ghost"
      className={cn(
        "w-full justify-between h-auto py-2 px-2",
        row.isCurrent && "bg-accent",
        row.occupiedBy && "border border-orange-300/60 bg-orange-50/40 dark:bg-orange-950/20 hover:bg-orange-100/50",
      )}
      onClick={() => handleSelect(row)}
    >
      <div className="flex items-center gap-2 min-w-0">
        {row.isCurrent && <Check className="w-4 h-4 text-green-600 flex-shrink-0" />}
        {row.occupiedBy && <ArrowRightLeft className="w-3.5 h-3.5 text-orange-600 flex-shrink-0" />}
        <div className="text-left min-w-0">
          <div className="font-medium truncate">{row.name}</div>
          {row.occupiedBy && (
            <div className="text-[10px] text-orange-700 dark:text-orange-400 truncate">
              Échanger avec {row.occupiedBy.customerName} ({row.occupiedBy.numberOfPeople}p)
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          <Users className="w-3 h-3" />
          {row.capacity}
        </span>
        {getCapacityBadge(row.capacity)}
      </div>
    </Button>
  );

  const freeTables = tableRows.filter(r => !r.occupiedBy);
  const swapTables = tableRows.filter(r => r.occupiedBy);
  const freeCombined = combinedRows.filter(r => !r.occupiedBy);
  const swapCombined = combinedRows.filter(r => r.occupiedBy);

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPendingSwap(null); }}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogContent className="max-w-xs p-0">
          <DialogHeader className="px-3 pt-3 pb-0">
            <DialogTitle className="text-sm font-medium">Changer de table</DialogTitle>
            <p className="text-xs text-muted-foreground">
              {reservation.persons} personne{reservation.persons > 1 ? 's' : ''} • {reservation.time}
            </p>
            {reservation.isLocked && (
              <p className="text-[10px] text-amber-700 dark:text-amber-400 flex items-center gap-1">
                <Lock className="w-3 h-3" /> Réservation verrouillée — échange désactivé
              </p>
            )}
          </DialogHeader>

          <div className="max-h-[360px] overflow-y-auto">
            {freeTables.length > 0 && (
              <div className="p-2">
                <p className="text-xs text-muted-foreground px-2 mb-1">Tables individuelles</p>
                {freeTables.map(renderOption)}
              </div>
            )}

            {freeCombined.length > 0 && (
              <div className="p-2 border-t">
                <p className="text-xs text-muted-foreground px-2 mb-1 flex items-center gap-1">
                  <Layers className="w-3 h-3" />
                  Tables combinées
                </p>
                {freeCombined.map(renderOption)}
              </div>
            )}

            {(swapTables.length > 0 || swapCombined.length > 0) && (
              <div className="p-2 border-t bg-orange-50/30 dark:bg-orange-950/10">
                <p className="text-xs text-orange-700 dark:text-orange-400 px-2 mb-1 flex items-center gap-1">
                  <ArrowRightLeft className="w-3 h-3" /> Tables occupées — échanger
                </p>
                {swapTables.map(renderOption)}
                {swapCombined.map(renderOption)}
              </div>
            )}

            {freeTables.length === 0 && freeCombined.length === 0 && swapTables.length === 0 && swapCombined.length === 0 && (
              <div className="p-4 text-center text-muted-foreground">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune table disponible</p>
                <p className="text-xs">pour ce créneau</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <TableSwapConfirmDialog
        open={!!pendingSwap}
        onOpenChange={(o) => { if (!o && !swapSubmitting) setPendingSwap(null); }}
        draggedReservation={pendingSwap ? {
          id: String(reservation.id),
          customerName: reservation.customerName || "Cette réservation",
          numberOfPeople: reservation.persons,
          tableName: reservation.tableId
            ? (tables.find(t => t.id === reservation.tableId)?.name || "—")
            : reservation.combinedTableId
              ? (combinedTables.find(c => c.id === reservation.combinedTableId)?.name || "—")
              : "Non assignée",
          tableId: reservation.tableId ?? null,
          combinedTableId: reservation.combinedTableId ?? null,
        } : null}
        targetReservation={pendingSwap ? {
          id: pendingSwap.target.reservationLocalId,
          customerName: pendingSwap.target.customerName,
          numberOfPeople: pendingSwap.target.numberOfPeople,
          tableName: pendingSwap.targetTableName,
          tableId: pendingSwap.target.tableId ?? null,
          combinedTableId: pendingSwap.target.combinedTableId ?? null,
        } : null}
        onConfirm={confirmSwap}
      />
    </>
  );
};
